import { Component, computed, inject, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { ListState } from '@modules/messages/types/list-state';
import { MessageFolder } from '@modules/messages/types/message-folder';

import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { itemActivityTenant } from '@modules/common/components/item-workspace/item-activity-tenant';
import { AbstractMainViewComponent } from '@modules/common/components/main-view/main-view.component';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { getStitchInstanceByStitchType } from '@modules/common/utils/stitch';
import { LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { AccountKindService } from '@frontendlib/common/services/account-kind.service';

import { StitchType } from '@sharedlib/common/types/StitchType';
import type { Message } from '@modules/messages/types/message';

@Component({
  selector: 'app-messages2-main-view',
  templateUrl: './messages-main-view.component.html',
  styleUrls: ['./messages-main-view.component.less'],
  standalone: false,
  // The reader provides its own; this one names the coming email's mailbox for its skeleton.
  providers: [WorkspaceIdentityService],
})
export class MessagesMainViewComponent extends AbstractMainViewComponent<
  StitchType.MESSAGE | StitchType.MESSAGE_FOLDER,
  Message | MessageFolder,
  FullFormBaseComponent<
    StitchType.MESSAGE | StitchType.MESSAGE_FOLDER,
    Message | MessageFolder,
    Record<string, AbstractControl>
  >,
  ListState
> {
  protected override readonly stateService = inject(MailAppStateService);

  private readonly injector = inject(Injector);
  private readonly messagesService = inject(MessagesService);
  private readonly shownId = signal<string | null>(null);
  private readonly accountKind = inject(AccountKindService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly workspaceIdentity = toSignal(
    inject(WorkspaceIdentityService)
      .current()
      .pipe(catchError(() => of(undefined))),
    { initialValue: undefined },
  );

  /**
   * The message an open is reading, shown in place of the pane from the click on (`MailAppStateService.pendingMessage`)
   * instead of leaving the previous message, or the empty state, on screen while the message is read.
   * Not while the reader on screen has unsaved changes: that reader stays until the user has answered whether to
   * leave it (`confirmUnsavedChanges` below), and the check is read when the pending message changes.
   */
  protected readonly pendingMessage = computed(() => {
    const pending = this.stateService.pendingMessage();
    if (!pending || pending.id === this.shownId() || this.formChangesModalsService.hasUnsavedChanges()) {
      return null;
    }
    return pending;
  });

  /**
   * Which of the team's two mailboxes the coming email is in, decided as the reader decides it
   * (`message-form.component.ts`: `itemWorkspace`), so the skeleton draws the same team header; none outside a team.
   */
  protected readonly pendingWorkspace = computed(() =>
    itemWorkspace(this.workspaceIdentity(), this.pendingMessage() ?? undefined),
  );

  /** Whether the reader will offer the hand-over (Assign, Responsible): a team login on a team-mailbox email. */
  protected readonly pendingHandover = computed(() => this.accountKind.isTeam() && this.pendingWorkspace() === 'team');

  /** Whether the reader will add its Activity tab after Comments: the same rule, on the same space context. */
  protected readonly pendingActivity = computed(
    () => !!itemActivityTenant(this.spaceContextStore, this.pendingMessage() ?? undefined),
  );

  /**
   * Whether the reader that is coming will show its Knowledge box, so the skeleton holds that space and the body
   * does not move when the message arrives: the reader's own toggle (`FullFormBaseComponent.displayKnowledgeState`,
   * variant `default.message`), read afresh for each open because a reader may have changed it since.
   */
  protected readonly pendingKnowledgeShown = computed(
    () =>
      !!this.pendingMessage() &&
      new LocalStorageItem<boolean>('ff-display-knowledge', {
        lsKey: 'ff-display-knowledge',
        parse: ({ raw }) => raw !== false && raw !== 'false',
      }).getSync(`default.${StitchType.MESSAGE}`) !== false,
  );

  /**
   * Overrides the base class's `getMainView()` wiring to keep this surface's original
   * `confirmUnsavedChanges` semantics: unlike the base class (which passes `ignoreCurrent: true` to
   * check for OTHER dirty forms before navigating away), this checks whether the incoming item's OWN
   * form entry is dirty. Preserved as-is rather than silently adopting the base class's semantics.
   */
  public override ngOnInit(): void {
    this.stateService
      .getSection()
      .pipe(takeUntil(this.alive))
      .subscribe((tabs) => this.section.set(tabs));

    this.stateService
      .getMainView()
      .pipe(
        takeUntil(this.alive),
        // Restoring the visible item after Stay must not ask about that same editor again.
        filter((item) => item !== this.item),
        switchMap((nextItem) =>
          this.formChangesModalsService
            .confirmUnsavedChanges(nextItem?.id)
            .pipe(map((canLeave) => ({ canLeave, nextItem }))),
        ),
        tap(({ canLeave }) => {
          if (!canLeave) this.stateService.setMainView(this.item);
        }),
        filter(({ canLeave }) => canLeave),
        map(({ nextItem }) => nextItem),
      )
      .subscribe((item) => {
        this.item = item;
        this.shownId.set(item?.id ?? null);
      });
  }

  protected handleNewMessage(): void {
    this.messagesService.composeMessage({ injector: this.injector });
  }

  protected handleNewFolder(): void {
    this.openCreateModal(getStitchInstanceByStitchType(StitchType.MESSAGE_FOLDER));
  }

  public asMessage(item: Message | MessageFolder): Message {
    return item as Message;
  }

  public asMessageFolder(item: Message | MessageFolder): MessageFolder {
    return item as MessageFolder;
  }
}
